import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../css/EnterpriseDetails.css";
import "../../css/DocumentHistoryResponsive.css";
import { getFilteredDocs } from "../../utils/approvedContentTabs";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";

// Icons
import viewIcon from "../../assets/UserIcons/ViewIcon.svg";
import folderIcon from "../../assets/Images/folder.png";
import downloadIcon from "../../assets/Images/Download.png";
import filterIcon from "../../assets/Images/Filter.png";
import changeStatusIcon from "../../assets/icons/changeStatusIcon.png";
import deleteIcon from "../../assets/UserIcons/DeleteIcon.svg";
import trainDoc from "../../assets/UserIcons/FileIcon.svg";
import trainText from "../../assets/icons/TrainText.png";
import trainUrls from "../../assets/UserIcons/Website.svg";
import trainVideo from "../../assets/UserIcons/VideoIcon.svg";
import trainQA from "../../assets/icons/TrainQA.png";
import searchIcon from "../../assets/UserIcons/Search.svg";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import { SortableHeader } from "../../components/SortableHeader";
import { useTableSort } from "../../hooks/useTableSort";

const UrlsHistory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [allApprovedDocs, setAllApprovedDocs] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname;

  const tabs = [
    { name: "Documents", path: "/user/knowledge-repository" },
    { name: "Web URLs", path: "/user/knowledgerepository-weburls" },
    { name: "Video URLs", path: "/user/knowledgerepository-videourls" },
    // { name: "Deleted", path: "/user/knowledgerepository-deletedocs" },
    // { name: "Text", path: "/user/texthistory" },
    // { name: "Q&A", path: "/user/qahistory" },
  ];

  const [stats, setStats] = useState({
    user_files_active_count: 0,
    user_files_inactive_count: 0,
    uploaded_files_active_count: 0,
    uploaded_files_inactive_count: 0,
  });

  // ✅ Fetch stats
  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/stats`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setStats(data.data || {});
      } else {
        // showErrorToast(data.message || "Failed to load document stats");
      }
    } catch (error) {
      console.error("❌ Error fetching stats:", error);
      showErrorToast("Something went wrong while fetching stats");
    } finally {
      setIsLoading(false);
    }
  };

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchGlobalUserDocs = useCallback(async (opts = {}) => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/global-user-docs`,
        {
          params: {
            page: opts.page ?? currentPage,
            limit: opts.limit ?? entriesPerPage,
            search: opts.search ?? searchTerm,
            dayFilter: opts.dayFilter ?? dayFilter,
            sortOrder: opts.sortOrder ?? sortOrder,
            fileType: "webpage",
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAllApprovedDocs(res.data);
      const headerTotal = Number(res.headers?.["x-total-count"]);
      setTotalEntries(
        Number.isFinite(headerTotal) ? headerTotal : (res.data || []).length,
      );

      // console.log("✅ API data:", res.data);
    } catch (error) {
      console.error("❌ Error fetching approved docs:", error);
      showErrorToast(TOAST_MESSAGES.URL_HISTORY.FETCH_FAILED);
    }
  }, [
    currentPage,
    entriesPerPage,
    searchTerm,
    dayFilter,
    sortOrder,
    token,
  ]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dayFilter, sortOrder]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalEntries / entriesPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [totalEntries, entriesPerPage, currentPage]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchGlobalUserDocs().catch((err) =>
        console.error("fetchGlobalUserDocs crash:", err),
      );
    }, 250);

    return () => clearTimeout(timeout);
  }, [fetchGlobalUserDocs]);

  const handleDeleteDocument = async (fileId) => {
    // if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await api.patch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/docs/soft-delete/${fileId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      showSuccessToast("URL deleted successfully");
      // Refresh data after delete
      fetchGlobalUserDocs();
    } catch (error) {
      console.error("❌ Error deleting document:", error);
      showErrorToast("Failed to delete document");
    }
  };

  const urlFiles = allApprovedDocs.map((w) => ({
    name: w.name,
    characters: w.total_tokens || 0,
    python_file_uuid: w.python_file_uuid,
    approval_status: w.approval_status,
    url_status: w.approval_status,
    file_status: w.file_status,
    uploaded_by: w.uploaded_by,
    updatedDate: w.updatedAt,
    createdAt: w.createdAt,
    action: "View",
    document_mode: w.document_mode,
    document_id: w.document_id,
    mode: w.mode,
  }));

  const totalPages = Math.max(1, Math.ceil(totalEntries / entriesPerPage));
  const currentData = urlFiles;
  const showingFrom =
    totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const showingTo = Math.min(currentPage * entriesPerPage, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const statsData = [
    {
      label: "Uploaded Documents",
      value: getFilteredDocs(allApprovedDocs, "document").length,
      icon: trainDoc,
      iconClass: "train-doc-icon",
    },
    {
      label: "Uploaded Web URLs",
      value: getFilteredDocs(allApprovedDocs, "webpage").length,
      icon: trainUrls,
    },
    // {
    //   label: "Uploaded Text",
    //   value: allApprovedDocs.filter((doc) => doc.type === "text").length,
    //   icon: trainText,
    // },
    {
      label: "Uploaded Video URLs",
      value: allApprovedDocs.filter((doc) => doc.type === "youtube_audio")
        .length,
      icon: trainVideo,
    },
    // {
    //   label: "Uploaded Q&A",
    //   value: allApprovedDocs.filter((doc) => doc.type === "qa").length,
    //   icon: trainQA,
    // },
    // {
    //   label: "Deleted Documents",
    //   icon: deleteIcon,
    //   value: stats.uploaded_files_inactive_count || 0,
    // },
  ];

  const handleChangeFileStatus = async (python_file_uuid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/update_file_status/${python_file_uuid}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // ❌ No body sent → Backend toggles automatically
        },
      );

      const result = await response.json();

      if (!response.ok) {
        showErrorToast(result.message || "Failed to update file status");
        return;
      }

      showSuccessToast("Status updated successfully");

      // refresh file list
      fetchGlobalUserDocs();
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast("Something went wrong");
    }
  };

  // ✅ Utility to detect and render clickable links (without "URL:" prefix)
  const renderWithLinks = (text) => {
    if (!text) return null;

    // Remove "URL:" prefix if exists
    const cleanText = text.replace(/^URL:\s*/i, "");

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return cleanText.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
          style={{ color: "#0d6efd", fontWeight: 500 }}
        >
          {part}
        </a>
      ) : (
        part
      ),
    );
  };

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm  d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center  mb-2">
              {item.icon && (
                <img
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                  // className="icon-image"
                  className={`icon-image  ${item.iconClass || ""}`}
                />
              )}
              <h6 className="ms-2 ak-text-sm mb-0">{item.label}</h6>
            </div>

            <h2 className="fw-bold ak-text-sm">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary ">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold mobile-heading ak-text-lg">
          Uploaded Web URLs
        </span>
      </div>

      <div className="mt-3 tabs-wrapper ">
        <div className="tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-button ${
                currentTab === tab.path ? "active" : " "
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>
      {urlFiles?.length === 0 ? (
        <></>
      ) : (
        <div
          style={{ borderLeft: "1px solid var(--dark-color) " }}
          className="d-flex px-2 py-1 justify-content-between align-items-center "
        >
          <div className="d-flex align-items-center">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={handleEntriesChange}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="ms-2 ak-text-md mt-2">Entries per page</label>
          </div>

          {/* Search input */}
          <div className="search">
            <div
              className="input-group custom-search-group"
              style={{ width: "320px" }}
            >
              <span className="input-group-text">
                {/* <i className="bi bi-search"></i> */}
                <img className="icon-image" src={searchIcon} alt="Search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted ak-text-md profile-i">
            {" "}
            {/* There are no web URL(s) in this section. */}
            No websites have been trained yet.
          </p>
        </div>
      ) : (
        <div
          className="not-disable ak-text-md"
          style={{ borderLeft: "1px solid var(--dark-color) " }}
        >
          <div className="table-responsive">
            <table className="table DocUrls-table table-sm w-100">
              <thead className="text-left">
                <tr>
                  <SortableHeader
                    column="name"
                    label="Web URLs"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="characters"
                    label="Total Characters"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="url_status"
                    label="URL Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* VKD58 started */}

                  <SortableHeader
                    column="mode"
                    label="Mode"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* VKD58 Ended */}

                  {/* <SortableHeader
                    column="uploaded_by"
                    label="Uploaded By"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  /> */}

                  <SortableHeader
                    column="file_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* DATE COLUMNS → also automatically sortable */}
                  <SortableHeader
                    column="createdAt"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updatedAt"
                    label="Last Updated On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((url, index) => (
                  <tr key={url.id || index}>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "150px" }}
                      title={url.name}
                    >
                      {renderWithLinks(url.name)}
                    </td>
                    <td className="text-left">{url.characters}</td>

                    <td className="text-left">
                      <span
                        className={`badge ${
                          url.url_status === "approved"
                            ? "bg-success"
                            : url.url_status === "pending"
                              ? "bg-warning"
                              : "bg-danger"
                        }`}
                      >
                        {url.url_status
                          ? url.url_status.charAt(0).toUpperCase() +
                            url.url_status.slice(1)
                          : "Unknown"}
                      </span>
                    </td>

                    {/* Document Mode column */}

                    {/* <td className="text-left" data-label="Document Mode">
                      <span
                        className="px-2 py-1 text-sm rounded-md text-[var(--primary-color)]"
                        style={{ display: "inline-block" }}
                      >
                        {url.document_mode?.toLowerCase() === "offline"
                          ? "Full Privacy"
                          : "Zero Privacy"}
                      </span>
                    </td> */}

                    {/* Mode column */}
                    <td className="text-left" data-label="Mode">
                      <span
                        className="px-2 py-1 text-sm rounded-md text-[var(--primary-color)]"
                        style={{ display: "inline-block" }}
                      >
                        {(url.mode || "Personal").charAt(0).toUpperCase() +
                          (url.mode || "Personal").slice(1)}
                      </span>
                    </td>

                    <td className="text-left">
                      <span
                        className={`badge ${
                          url.file_status === "1"
                            ? "bg-success" // GREEN
                            : "bg-warning" // YELLOW
                        }`}
                      >
                        {url.file_status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* <td>
                      {(url.uploaded_by || "User").charAt(0).toUpperCase() +
                        (url.uploaded_by || "User").slice(1)}
                    </td> */}
                    <td className="text-left" data-label="Updated At">
                      {/* {new Date(url.createdAt).toLocaleString()} */}
                      {formatDateToUserTimeZone(url.createdAt)}
                    </td>
                    <td className="text-left">
                      {/* {new Date(url.updatedDate).toLocaleString()} */}
                      {formatDateToUserTimeZone(url.updatedDate)}
                    </td>
                    <td className="text-center">
                      {/* <button className="icon-button" title="Change Status">
                        <img src={changeStatusIcon} alt="Change Status" />
                      </button> */}
                      {/* <button
                        onClick={() =>
                          handleDeleteDocument(url.python_file_uuid)
                        }
                        className="icon-button"
                        title="Delete"
                        disabled={
                          url.approval_status === "approved" ||
                          url.approval_status === "rejected"
                        }
                      >
                        <img
                          className="icon-image"
                          src={deleteIcon}
                          alt="Delete"
                        />
                      </button> */}
                      {/* <button
                        onClick={() =>
                          handleChangeFileStatus(url.python_file_uuid)
                        }
                        className="icon-button"
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button> */}
                      <button
                        onClick={() =>
                          url.mode?.toLowerCase() !== "global" &&
                          handleChangeFileStatus(url.python_file_uuid)
                        }
                        className={`icon-button ${
                          url.mode?.toLowerCase() === "global"
                            ? "disabled-icon"
                            : ""
                        }`}
                        title={
                          url.mode?.toLowerCase() === "global"
                            ? "Status change disabled for Global mode"
                            : "Change Status"
                        }
                        disabled={url.mode?.toLowerCase() === "global"}
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                          style={{
                            opacity:
                              url.mode?.toLowerCase() === "global" ? 0.4 : 1,
                            cursor:
                              url.mode?.toLowerCase() === "global"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>

          
        </div>
      )}
    </div>
  );
};

export default UrlsHistory;
